/**
 * 使うときだけ読む機能（2026-09-27 sdk-light・docs/PERFORMANCE.md の「ブラウザ SDK」）。
 *
 * WHY: X で見て → タップして → X のアプリ内ブラウザですぐ遊べる、が Kumo で最重要。起動に要るのは
 * 初期化・ゲストのサインイン・セーブ・stats くらいで、マルチプレイ・課金・広告・ソーシャル・運営機能を使わないゲームも多い。
 * そこで核（/sdk.js・/sdk.mjs）にはそれだけを入れ、残りは最初に呼ばれたときに /sdk/<機能>-<hash>.mjs を取りに行く。
 *
 * しくみ:
 *   - ソースは普通の `import('./store.js')`。ビルド（build.mjs）がこれを「別ファイル（dist/sdk/store-<hash>.mjs）への参照」に置き換える。
 *     ESM（/sdk.mjs・npm の dist/kumo.mjs）は相対の './sdk/…' のまま。`<script src>`（IIFE）は /sdk.js の URL を基準にした
 *     絶対 URL で読む（相対は別オリジンで解決できない。下の __KUMO_IIFE_CHUNKS__）
 *   - 機能のファイル同士・核とはコードを共有しない（KumoError・Emitter の小さなコピーを各自が持つ）。共有すると、
 *     核を読む前に共有部分のファイルをもう 1 つ待つことになり、最初の表示が 1 往復遅れる。instanceof は core.ts の印で揃える
 *   - テスト（tsx）とバンドラ（Vite・webpack）では、この import() がそのまま動く（バンドラは自分のチャンクにする）
 *
 * 公開 API は変えない（窓口 = 下の lazy* が元の create* と同じ形のオブジェクトを返す）:
 *   - もともと Promise を返すもの → 読んでから本体を呼ぶ（読むのに失敗したら KumoError('sdk_load_failed')）
 *   - 同期のもの（on・getter・状態を変えるだけの関数）→ 読み込みのきっかけにしない。まだ読んでいなければ
 *     「読んでいない = まだ何も始まっていない」の値を返す / 読めたときに順番どおりに流す
 */

import { KumoError, type KumoCore } from './core.js';
import type { P2POptions, Realtime, Rooms } from './rooms.js';
import type { KumoSocial } from './social.js';
import type { KumoLiveOps } from './liveops.js';

/**
 * `<script src=…/sdk.js>`（IIFE）のときだけ build.mjs が define で埋める: 機能の名前 → ファイル名（store-<hash>.mjs）。
 * ESM（/sdk.mjs・npm）とテスト（tsx）では undefined = ソースどおり import('./store.js')（ビルドでは './sdk/store-<hash>.mjs'）を使う。
 *
 * WHY IIFE だけ絶対 URL か（2026-09-27 sdk-light-fix。統合後にリーダーがアプリ内ブラウザで発見）:
 *   crossorigin 属性なしの別オリジンの <script src> は「CORS-cross-origin のスクリプト」で、その中の import() は
 *   基準の URL が about:blank になる（HTML の仕様: スクリプト自身の URL が基準になるのは CORS-same-origin のときだけ）。
 *   相対の './sdk/…' は解決できず、リクエストも出ずに失敗していた = 自前サーバーのゲーム（別オリジン・普通の使い方）で
 *   課金・広告・ソーシャル・マルチプレイがすべて動かなかった。絶対 URL の import() は動く。
 *   ESM はモジュールスクリプト（CORS で取得）なので相対で正しく解決され、バンドラも相対の文字列でないと辿れないので、そのままにする
 */
type IifeChunks = { __KUMO_IIFE_CHUNKS__?: Record<string, string> };

/**
 * このスクリプト（/sdk.js）の URL。読み込みの最中にしか分からない（document.currentScript）ので、評価した瞬間に取っておく。
 * kumo-boot.js の createElement('script') で入れた場合も取れる。取れない（インラインに貼った等）ときは apiUrl を使う
 */
const SCRIPT_URL = (() => {
  try {
    return (globalThis.document?.currentScript as HTMLScriptElement | null)?.src || undefined;
  } catch {
    return undefined;
  }
})();

/** IIFE での機能のファイルの絶対 URL。/sdk.js の隣の sdk/ フォルダ（CDN の dist/kumo.js なら dist/sdk/） */
export function iifeChunkUrl(file: string, apiUrl: string, scriptUrl = SCRIPT_URL): string {
  return new URL(`sdk/${file}`, scriptUrl ?? `${apiUrl.replace(/\/+$/, '')}/sdk.js`).href;
}

/** 機能のファイルの読み込み（同じページで 1 回だけ）。失敗したら覚えず、次の呼び出しで読み直す（電波が戻れば動く） */
const chunks = new Map<string, Promise<unknown>>();
export function loadChunk<T>(name: string, load: () => Promise<T>, apiUrl: string): Promise<T> {
  let p = chunks.get(name) as Promise<T> | undefined;
  if (!p) {
    // define の置き換えがここで直接効くよう、変数に入れずに書く（ESM では undefined になり、下の分岐ごと消える）
    const file = (globalThis as IifeChunks).__KUMO_IIFE_CHUNKS__?.[name];
    const run = file ? () => import(/* @vite-ignore */ iifeChunkUrl(file, apiUrl)) as Promise<T> : load;
    p = run().catch((e: unknown) => {
      chunks.delete(name);
      throw new KumoError(0, 'sdk_load_failed', `Could not load the "${name}" part of the SDK (network?). Try again.`, { feature: name, cause: e });
    });
    chunks.set(name, p);
  }
  return p;
}

interface Pending<T> {
  apply(v: T): void;
  /** ある = 読み込みの失敗で捨てる呼び出し（Promise を返したもの）。無い = 次に読めたときまで残す（on の登録） */
  fail?(e: unknown): void;
}

/** 1 つの機能の本体（読むまでは null）と、読むまでの呼び出しの列 */
export class Lazy<T> {
  value: T | null = null;
  private loading = false;
  private queue: Pending<T>[] = [];
  constructor(private readonly make: () => Promise<T>) {}

  get started() {
    return this.value !== null || this.loading;
  }

  private load() {
    if (this.started) return;
    this.loading = true;
    this.make().then(
      (v) => {
        this.value = v;
        this.loading = false;
        // 読むまでに来た呼び出しを、来た順に流す（元の同期の API と同じ順序になる）
        for (const p of this.queue.splice(0)) {
          try {
            p.apply(v);
          } catch (e) {
            console.error('[game-backend]', e);
          }
        }
      },
      (e: unknown) => {
        this.loading = false;
        const failed = this.queue.filter((p) => p.fail);
        this.queue = this.queue.filter((p) => !p.fail);
        for (const p of failed) p.fail!(e);
      }
    );
  }

  /** Promise を返す API。読めていれば今すぐ（同期で）本体を呼ぶ = クリックのハンドラの中の処理がそのまま動く */
  call<R>(fn: (v: T) => Promise<R>): Promise<R> {
    if (this.value) return fn(this.value);
    return new Promise<R>((resolve, reject) => {
      this.queue.push({
        apply: (v) => {
          try {
            resolve(fn(v));
          } catch (e) {
            reject(e);
          }
        },
        fail: reject
      });
      this.load();
    });
  }

  /** 戻り値の無い同期の API で、機能を動かし始めるもの（presence.start）。読み込みの失敗は警告だけ（元も例外を投げない） */
  run(fn: (v: T) => void): void {
    if (this.value) return fn(this.value);
    this.queue.push({ apply: fn, fail: (e) => console.warn('[game-backend]', e) });
    this.load();
  }

  /**
   * 戻り値の無い同期の API で、動いているものを変える・止めるもの（disconnect・presence.stop・gameplayStart）。
   * まだ読み始めてもいない = 何も動いていないので何もしない。読んでいる途中なら順番どおりに後で流す
   */
  ifStarted(fn: (v: T) => void): void {
    if (this.value) return fn(this.value);
    if (this.loading) this.queue.push({ apply: fn, fail: () => undefined });
  }

  /**
   * イベントの登録。読み込みのきっかけにしない（起動時に on だけ書くゲームで、使わない機能を読ませない）。
   * イベントは機能を使って初めて起きるので、読めたときに本体へ登録すれば取りこぼさない
   */
  on(subscribe: (v: T) => () => void): () => void {
    if (this.value) return subscribe(this.value);
    let off: (() => void) | null = null;
    const pending: Pending<T> = { apply: (v) => void (off = subscribe(v)) };
    this.queue.push(pending);
    return () => {
      const i = this.queue.indexOf(pending);
      if (i >= 0) this.queue.splice(i, 1);
      off?.();
    };
  }
}

type AsyncFn = (...args: never[]) => Promise<unknown>;

/** Promise を返す関数だけの入れ物（friends・guilds・gifts など）を窓口にする */
function asyncGroup<T>(l: Lazy<unknown>, pick: (v: never) => T, keys: ReadonlyArray<keyof T & string>): T {
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    out[k] = (...args: never[]) => l.call((v) => (pick(v as never)[k] as AsyncFn)(...args));
  }
  return out as T;
}

// ---- マルチプレイ（本体 rooms.ts / roomsClient.ts は Core の持ち物。ここは読み込みの境目だけ） ----

/**
 * kumo.rooms と kumo.realtime は 1 つの本体（同じ WebSocket・rooms.ts の createRoomsAndRealtime）。同じ core には同じ Lazy を返す
 * （別々に読むと接続が 2 本になり、サーバーが古い方を 4004 で閉じる）
 */
const roomsLazies = new WeakMap<KumoCore, Lazy<{ rooms: Rooms; realtime: Realtime }>>();
/**
 * kumo.rooms.setP2POptions の入れ物（core ごと）。本体に同じ入れ物を渡す（RoomsOptions.p2p）= 読み込みの前に呼ばれた値も、後の値も同じように効く。
 * WHY 読み込みのきっかけにしないか: 設定だけで rooms の chunk を読みに行かない（起動の速さ。heldRoom 等の同期の getter と同じ方針）
 */
const p2pOptionsByCore = new WeakMap<KumoCore, P2POptions>();
function p2pOptionsOf(core: KumoCore): P2POptions {
  let o = p2pOptionsByCore.get(core);
  if (!o) p2pOptionsByCore.set(core, (o = {}));
  return o;
}
function roomsLazy(core: KumoCore) {
  let l = roomsLazies.get(core);
  if (!l) {
    l = new Lazy(() => loadChunk('rooms', () => import('./rooms.js'), core.apiUrl).then((m) => m.createRoomsAndRealtime(core, { p2p: p2pOptionsOf(core) })));
    roomsLazies.set(core, l);
  }
  return l;
}

export function lazyRooms(core: KumoCore): Rooms {
  const both = roomsLazy(core);
  // 以下は本体の rooms を指す窓口（both.value.rooms）。Lazy の API をそのまま使うため、rooms だけを見る薄い包みを作る
  const l = {
    get value() {
      return both.value ? both.value.rooms : null;
    },
    call: <R>(fn: (r: Rooms) => Promise<R>) => both.call((v) => fn(v.rooms)),
    on: (subscribe: (r: Rooms) => () => void) => both.on((v) => subscribe(v.rooms)),
    ifStarted: (fn: (r: Rooms) => void) => both.ifStarted((v) => fn(v.rooms))
  };
  return {
    quickMatch: (mode) => l.call((r) => r.quickMatch(mode)),
    cancelMatch: () => l.call((r) => r.cancelMatch()),
    create: (mode, opts) => l.call((r) => r.create(mode, opts)),
    join: (idOrCode) => l.call((r) => r.join(idOrCode)),
    list: (mode) => l.call((r) => r.list(mode)),
    // 読む前 = まだ一度も接続していない。本体も接続前は null / 'idle' を返す
    get heldRoom() {
      return l.value ? l.value.heldRoom : null;
    },
    fetchHeldRoom: () => l.call((r) => r.fetchHeldRoom()),
    // 先につなぐ口（2026-10-05 core/room-cold）。rooms の読み込みもここで始まる = 押す前に済む。読み込みの失敗（オフライン）でも投げない
    warmUp: () => l.call((r) => r.warmUp()).catch(() => false),
    rejoin: () => l.call((r) => r.rejoin()),
    // 同じタブの再読み込みからの復帰（2026-10-06 core/room-leave）。rooms の読み込みは始まる（マルチプレイのゲームがページを開いた時に呼ぶ = 先読みにもなる）。
    // 読み込みに失敗しても投げない（戻れない = null。本体と同じ約束）
    rejoinIfReloaded: () => l.call((r) => r.rejoinIfReloaded()).catch(() => null),
    releaseHeldRoom: () => l.call((r) => r.releaseHeldRoom()),
    get current() {
      return l.value ? l.value.current : null;
    },
    get connectionState() {
      return l.value ? l.value.connectionState : 'idle';
    },
    on: ((event: never, fn: never) => l.on((r) => r.on(event, fn))) as Rooms['on'],
    disconnect: () => l.ifStarted((r) => r.disconnect()),
    // 本体と同じ入れ物を書き換える（p2pOptionsOf の WHY）
    setP2POptions: (o) => void Object.assign(p2pOptionsOf(core), o)
  };
}

/**
 * kumo.realtime（リアルタイムの部屋・realtime.ts）。rooms と同じ本体（同じ読み込み・同じ接続）。
 * 同期の getter（channels・connectionState・anonymous）は読み込みのきっかけにしない（読む前 = まだ何も始まっていない値）
 */
export function lazyRealtime(core: KumoCore): Realtime {
  const both = roomsLazy(core);
  return {
    join: (name) => both.call((v) => v.realtime.join(name)),
    create: (opts) => both.call((v) => v.realtime.create(opts)),
    get channels() {
      return both.value ? both.value.realtime.channels : [];
    },
    get connectionState() {
      return both.value ? both.value.realtime.connectionState : 'idle';
    },
    get anonymous() {
      return both.value ? both.value.realtime.anonymous : false;
    },
    on: ((event: never, fn: never) => both.on((v) => v.realtime.on(event, fn))) as Realtime['on']
  };
}

// ---- ソーシャル（social.ts）と運営機能（liveops.ts） ----

export function lazySocial(core: KumoCore): Omit<KumoSocial, 'sanctions'> {
  // social / friends / … / presence は 1 つの本体（presence の定期送信の状態を共有する）
  const l = new Lazy(() => loadChunk('social', () => import('./social.js'), core.apiUrl).then((m) => m.createSocial(core)));
  const group = <K extends keyof KumoSocial>(key: K, keys: ReadonlyArray<keyof KumoSocial[K] & string>) =>
    asyncGroup<KumoSocial[K]>(l as Lazy<unknown>, (v: KumoSocial) => v[key], keys);
  return {
    // setNetworkPresence は social.ts から外した（network-gate。段階 ② で戻すときはここにも足す）
    social: group('social', ['profile', 'declareAge']),
    friends: group('friends', ['list', 'requests', 'request', 'accept', 'decline', 'cancel', 'remove', 'block', 'unblock', 'blocked']),
    presence: {
      ...group('presence', ['friends', 'set']),
      start: (s) => l.run((v) => v.presence.start(s)),
      stop: () => l.ifStarted((v) => v.presence.stop())
    },
    chat: group('chat', ['send', 'read']),
    guilds: group('guilds', ['create', 'search', 'mine', 'get', 'update', 'dissolve', 'join', 'leave', 'acceptRequest', 'declineRequest', 'kick', 'setRole']),
    reports: group('reports', ['submit'])
    // sanctions（本人の制裁の確認と異議）は核の sanctions.ts（小さい・BAN の後の起動ですぐ使う・常に出す）
  };
}

export function lazyLiveOps(core: KumoCore): KumoLiveOps {
  const l = new Lazy(() => loadChunk('liveops', () => import('./liveops.js'), core.apiUrl).then((m) => m.createLiveOps(core)));
  const group = <K extends keyof KumoLiveOps>(key: K, keys: ReadonlyArray<keyof KumoLiveOps[K] & string>) =>
    asyncGroup<KumoLiveOps[K]>(l as Lazy<unknown>, (v: KumoLiveOps) => v[key], keys);
  return {
    events: group('events', ['list']),
    loginBonus: group('loginBonus', ['status', 'claim']),
    gifts: group('gifts', ['list', 'claim', 'claimAll']),
    serials: group('serials', ['redeem']),
    gacha: group('gacha', ['list', 'pull'])
  };
}
